import { ExamRoomDetailsBar } from '@exam-center/exam-center-location-details';
import {
  getNestedValue,
  LOCATION_DETAILS,
  ROOM_INFORMATION,
  useGetRoomReservationSummary,
  type RoomReservationSummary,
} from '@exam-center/exam-location-management';
import { RoomStatus } from '@exam-center/graphql';
import { APP_PERMISSIONS } from '@exam-center/permission';
import { useNavigate } from '@exam-center/router';
import {
  Button,
  Divider,
  EditOutlinedIcon,
  Flex,
  PaperSection,
  SectionTitle,
  Text,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { WithPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useState } from 'react';

import { RetiredRoomInfo } from '../../components';
import { useAdminExamLocationDetail } from '../../providers';
import { AdminExamLocationDetailsUpdateRoomModal } from '../admin-exam-location-details-update-room-modal';
import { CannotRetireExamCenterRoomModal } from '../cannot-retire-exam-center-room-modal';
import { RetireExamCenterRoomLoadingModal } from '../retire-exam-center-room-loading-modal';
import { RetireExamCenterRoomModal } from '../retire-exam-center-room-modal';

export default function AdminExamCenterLocationDetails() {
  const navigate = useNavigate();
  const { examRoomInfo } = useAdminExamLocationDetail();
  const isRetired = examRoomInfo.status === RoomStatus.Retired;
  const [isOpenUpdateRoomModal, updateRoomModalHandler] = useDisclosure();
  const [isLoadingRetireModalOpen, loadingRetireModalHandlers] = useDisclosure(false);
  const [isRetireModalOpen, retireModalHandlers] = useDisclosure(false);
  const [isCannotRetireModalOpen, cannotRetireModalHandlers] = useDisclosure(false);
  const [retireReservations, setRetireReservations] = useState<RoomReservationSummary[]>([]);
  const { showSnackbar } = useSnackbar();
  const { checkReservations } = useGetRoomReservationSummary();

  const roomData = {
    room: {
      name: examRoomInfo.name ?? '-',
      building: examRoomInfo.building?.nameTh ?? '-',
      floor: examRoomInfo.floor ?? '-',
      roomNumber: examRoomInfo.roomNumber || '-',
      seatCount: examRoomInfo.seat ?? '-',
      location: {
        province: examRoomInfo.building?.location.province?.nameTh || '-',
        nameTh: examRoomInfo.building?.location.nameTh || '-',
        nameEn: examRoomInfo.building?.location.nameEn || '-',
        building: examRoomInfo.building?.nameTh || '-',
      },
    },
  };

  const handlePhysicalInfoClick = () => {
    navigate('/admin/exam-location/locations/:roomId/physical-info', {
      params: {
        roomId: examRoomInfo.id,
      },
    });
  };

  const handleComputerInfoClick = () => {
    navigate('/admin/exam-location/locations/:roomId/computer-info', {
      params: {
        roomId: examRoomInfo.id,
      },
    });
  };

  const handleRetireClick = async () => {
    loadingRetireModalHandlers.open();
    try {
      const result = await checkReservations(examRoomInfo.id);
      loadingRetireModalHandlers.close();

      if (!result.hasReservations) {
        retireModalHandlers.open();
      } else {
        setRetireReservations(result.reservations);
        cannotRetireModalHandlers.open();
      }
    } catch (error) {
      showSnackbar({
        message: 'ไม่สามารถตรวจสอบข้อมูลการใช้งานได้ กรุณาลองใหม่อีกครั้ง',
        variant: 'error',
      });
      loadingRetireModalHandlers.close();
      console.error(error);
    }
  };

  return (
    <Flex
      sx={(theme) => ({
        flexDirection: 'column',
        gap: theme.spacing(6),
        p: theme.spacing(6),
      })}
    >
      <PaperSection title='ข้อมูลสถานที่สอบ'>
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            p: theme.spacing(6),
          })}
        >
          <SectionTitle title='Location Details' />

          {LOCATION_DETAILS.map((detail) => (
            <Flex
              key={detail.label}
              sx={(theme) => ({
                gap: theme.spacing(6),
              })}
            >
              <Text
                variant='body1Semibold'
                sx={{
                  maxWidth: '240px',
                  width: '50%',
                }}
              >
                {detail.label}
              </Text>

              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  gap: theme.spacing(4),
                })}
              >
                {detail.value.map((accessor, index) => (
                  <Text key={index}>{String(getNestedValue(roomData, accessor) || '')}</Text>
                ))}
              </Flex>
            </Flex>
          ))}
        </Flex>
      </PaperSection>

      <PaperSection
        title={
          <Flex
            sx={{
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Text variant='h3'>ข้อมูลห้องสอบ</Text>
            {isRetired ? null : (
              <WithPermission
                requiredPermission={
                  APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION.UPDATE
                }
              >
                <Button
                  onClick={updateRoomModalHandler.open}
                  startIcon={<EditOutlinedIcon />}
                  variant='outlined'
                  color='secondary'
                >
                  แก้ไข
                </Button>
              </WithPermission>
            )}
          </Flex>
        }
      >
        <Flex
          sx={(theme) => ({
            flexDirection: 'column',
            gap: theme.spacing(6),
            p: theme.spacing(6),
          })}
        >
          <SectionTitle title='Room Information' />

          {ROOM_INFORMATION.map((information) => (
            <Flex
              key={information.label}
              sx={(theme) => ({
                gap: theme.spacing(6),
              })}
            >
              <Text
                variant='body1Semibold'
                sx={{
                  maxWidth: '240px',
                  width: '50%',
                }}
              >
                {information.label}
              </Text>
              <Flex
                sx={(theme) => ({
                  flexDirection: 'column',
                  gap: theme.spacing(4),
                })}
              >
                {information.value.map((accessor, index) => (
                  <Text key={index}>{String(getNestedValue(roomData, accessor) || '')}</Text>
                ))}
              </Flex>
            </Flex>
          ))}

          <Divider />

          <SectionTitle title='Room Details' />

          <ExamRoomDetailsBar
            examRoomDetailType='physical-info'
            onButtonClick={handlePhysicalInfoClick}
          />
          <ExamRoomDetailsBar
            examRoomDetailType='computer-info'
            onButtonClick={handleComputerInfoClick}
          />
        </Flex>
      </PaperSection>

      {isRetired ? null : (
        <Flex sx={{ justifyContent: 'flex-start' }}>
          <WithPermission
            requiredPermission={APP_PERMISSIONS.ADMIN_EXAM_LOCATION_MANAGEMENT.EXAM_LOCATION.UPDATE}
          >
            <Button color='error' onClick={handleRetireClick}>
              ยกเลิกการใช้งานสถานที่สอบ
            </Button>
          </WithPermission>
        </Flex>
      )}

      {isRetired ? <RetiredRoomInfo /> : null}

      {isOpenUpdateRoomModal ? (
        <AdminExamLocationDetailsUpdateRoomModal
          open={isOpenUpdateRoomModal}
          onClose={updateRoomModalHandler.close}
        />
      ) : null}

      <RetireExamCenterRoomLoadingModal open={isLoadingRetireModalOpen} />

      {isRetireModalOpen ? (
        <RetireExamCenterRoomModal
          open={isRetireModalOpen}
          onClose={retireModalHandlers.close}
          roomId={examRoomInfo.id}
        />
      ) : null}

      {isCannotRetireModalOpen ? (
        <CannotRetireExamCenterRoomModal
          open={isCannotRetireModalOpen}
          onClose={cannotRetireModalHandlers.close}
          reservations={retireReservations}
        />
      ) : null}
    </Flex>
  );
}
